import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

interface CreateDataPayload {
    name: string;
    code: string;
    status: 'active' | 'inactive';
}

interface PaymentMethod {
    id: string;
    name: string;
    code: string;
    status: 'active' | 'inactive';
}

interface Pagination {
    current_page: number;
    per_page: number;
    total: number;
    total_page: number;
}

@Component({
  selector: 'app-payment-method',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './payment-method.component.html',
  styleUrl: './payment-method.component.scss'
})
export class PaymentMethodComponent {
    isModalOpen = false;
    isEditMode = false;
    isSubmitting = false;
    selectedPaymentMethodId: string = '';

    paymentMethod: PaymentMethod[] = [];
    pagination: Pagination = {
        current_page: 1,
        per_page: 10,
        total: 0,
        total_page: 1
    };

    createDataForm: CreateDataPayload = {
        name: '',
        code: '',
        status: 'inactive'
    };

    constructor(private apiService: ApiService, private router: Router) { }

    ngOnInit(): void {
        this.getPaymentMethod();
    }

    async getPaymentMethod(): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/tenant/manage/payment-method/list?page=${this.pagination.current_page}&per_page=${this.pagination.per_page}`
            );
            // console.log('Payment Methods:', response);
            this.paymentMethod = response.data.list;
            this.pagination = response.data.pagination;
        } catch (error) {
            console.error('Error fetching payment methods:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Failed to fetch payment methods.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            })
        }
    }

    async getPaymentMethodById(paymentMethodId: string): Promise<void> {
        try {
            const response = await this.apiService.get(`/api/v1/tenant/manage/payment-method/${paymentMethodId}`);
            // console.log('Payment Method Details:', response);
            this.createDataForm = {
                name: response.data.name,
                code: response.data.code,
                status: response.data.status
            }
            this.selectedPaymentMethodId = paymentMethodId;
            this.isEditMode = true;
            this.isModalOpen = true;
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat mengambil data payment method.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            })
        }
    }

    async createData(): Promise<void> {
        if (!this.validateDataForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.post('/api/v1/tenant/manage/payment-method', this.createDataForm);
            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Payment method berhasil ditambahkan.',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false
            });
            this.closeModal();
            this.getPaymentMethod();
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat menambahkan payment method.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    async updateData(): Promise<void> {
        if (!this.validateDataForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.put(`/api/v1/tenant/manage/payment-method/${this.selectedPaymentMethodId}`, this.createDataForm);
            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Payment method berhasil diperbarui.',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false
            });
            this.closeModal();
            this.getPaymentMethod();
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat memperbarui payment method.',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false
            })
        } finally {
            this.isSubmitting = false;
        }
    }

    async deleteData(paymentMethodId: string, paymentMethodName: string): Promise<void> {
        const result = await Swal.fire({
            title: 'Konfirmasi Hapus',
            text: `Apakah Anda yakin ingin menghapus payment method ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Ya, Hapus',
            cancelButtonText: 'Batal',
        });
        if (result.isConfirmed) {
            try {
                await this.apiService.delete(`/api/v1/tenant/manage/payment-method`, paymentMethodId);
                await Swal.fire({
                    icon: 'success',
                    title: 'Berhasil',
                    text: 'Payment method berhasil dihapus.',
                    confirmButtonColor: '#28a745',
                    timer: 2000,
                    showConfirmButton: false
                });
                this.getPaymentMethod();
            } catch (error) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error?.error?.message || 'Terjadi kesalahan saat menghapus payment method.',
                    confirmButtonColor: '#dc3545',
                    timer: 2000,
                    showConfirmButton: false
                });
            }
        }
    }

    openaddModal(): void {
        this.isModalOpen = true;
        this.resetForm();
    }

    openeditModal(paymentMethodId: string): void {
        this.getPaymentMethodById(paymentMethodId);
    }

    closeModal(): void {
        this.isModalOpen = false;
        this.resetForm();
    }

    resetForm(): void {
        this.createDataForm = {
            name: '',
            code: '',
            status: 'inactive'
        };
    }

    validateDataForm(): boolean {
        if (!this.createDataForm.name || !this.createDataForm.code || !this.createDataForm.status) {
            Swal.fire({
                icon: 'warning',
                title: 'Peringatan',
                text: 'Semua field harus diisi.',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }
        return true;
    }

    async goToPage(page: number): Promise<void> {
        if (page < 1 || page > this.pagination.total_page) {
            return;
        }
        this.pagination.current_page = page;
        await this.getPaymentMethod();
    }

    async nextPage(): Promise<void> {
        await this.goToPage(this.pagination.current_page + 1);
    }

    async previousPage(): Promise<void> {
        await this.goToPage(this.pagination.current_page - 1);
    }

    // ==================== HANDLER SUBMIT ====================

    async onSubmit(): Promise<void> {
        if (this.isEditMode) {
            await this.updateData();
        } else {
            await this.createData();
        }
    }
}
